// src/routes/MediaSelection/MediaSelection.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Button,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Paper,
  styled,
  Chip,
  CircularProgress,
  Backdrop,
  Fade,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Container,
  Grid,
  Fab
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { bg, border, shadow, adaptiveColor } from '../../utils/darkModeAdapter';
import { motion, AnimatePresence } from 'framer-motion';
import ForumIcon from '@mui/icons-material/Forum';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CampaignIcon from '@mui/icons-material/Campaign';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { segmentedTabsContainer, segmentedTab, chatIconButton, menuIconButton, selectionCard, selectionCardButton, designTokens } from '../../theme/designTokens';

const mediaSelectionTheme = designTokens.featureThemes.media_selection;

import AppLayout from '../../components/layout/AppLayout';

// ------------------- 画面構成要素 -------------------
import SelectPlanScreen from './SelectPlanScreen';      // 採用計画選択画面
import MediaSelectionForm from './MediaSelectionForm';  // 媒体選定フォーム
import RequirementsView from './RequirementsView';      // 入力内容確認用
import MediaSelectionResult from './MediaSelectionResult'; // GPT結果／媒体選定結果

import AIChat from '../../components/AIChat';
import AIChatSide from '../../components/AIChatSide';
import AssistMenu from './AssistMenu';           // アシストメニュー
import LoadingScreen from '../../components/LoadingScreen';
import { activityTracker } from '../../utils/activityTracker';

const API_BASE_URL = process.env.REACT_APP_API_URL;

function saveToLocalStorage(planAData, planBData, adviceObj, formValues, resultId, scientificAnalysis = null) {
  // 保存するデータ構造を整理
  const mediaResultObject = {
    resultId,
    gptAdvice: {
      planA: planAData,
      planB: planBData,
      recommendation: adviceObj?.recommendation || adviceObj?.advice || "",
      reason: adviceObj?.reason || "",
      merits: adviceObj?.merits || "",
      demerits: adviceObj?.demerits || "",
      scientificAnalysis: scientificAnalysis  // 科学的分析を追加
    }
  };

  localStorage.setItem("lastMediaSelection", JSON.stringify({
    mediaResult: mediaResultObject,
    formValues
  }));
}

// ============= セグメントコントロール型Tabs =============
const CustomTabs = styled(Tabs)(({ theme }) => ({
  ...segmentedTabsContainer('media_selection'),
}));

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    ...segmentedTab('media_selection'),
  })
);

function NiceTabsExample({ tabValue, setTabValue }) {
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  return (
    <CustomTabs
      value={tabValue}
      onChange={handleTabChange}
      variant="fullWidth"
      textColor="inherit"
      indicatorColor="secondary"
    >
      <CustomTab label="入力内容" />
      <CustomTab label="媒体選定結果" />
    </CustomTabs>
  );
}

// ================= 定数 =================
// USE_MOCK を false にすると実際のGPT API呼び出しを行います
const USE_MOCK = false;
const BACKEND_URL = process.env.REACT_APP_API_URL;

// ================= メインコンポーネント =================
export default function MediaSelection() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  // 認証とトークン管理
  const { user: currentUser, loading: authLoading } = useAuth();

  // --- UIモード管理: "selection" | "selectPlan" | "loadingPlan" | "form" | "loading" | "main"
  const [uiMode, setUiMode] = useState('selection');
  // 選択されたプランID（nullなら新規入力）
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  // フォーム入力データ
  const [formValues, setFormValues] = useState({});
  // 媒体選定結果（GPTの結果など）
  const [mediaResult, setMediaResult] = useState(null);

  // Drawer（サイドバー）

  // チャット関連
  const [chatMode, setChatMode] = useState('off');
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);
  const [brushupLoading, setBrushupLoading] = useState(false);

  // Snackbar通知
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // 履歴ダイアログ
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const handleCloseHistory = () => setHistoryOpen(false);

  // 採用計画確定ダイアログ
  const [decideDialogOpen, setDecideDialogOpen] = useState(false);
  const handleCloseDecideDialog = () => setDecideDialogOpen(false);

  // 削除確認ダイアログ
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // AssistMenu 管理
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // ---- planA, planB, adviceObj, isLoading ----
  const [mediaPlanA, setMediaPlanA] = useState(null);
  const [mediaPlanB, setMediaPlanB] = useState(null);
  const [adviceObj, setAdviceObj] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reqBudget, setReqBudget] = useState(0);

  // 再選定関連の状態
  const [isRegenerating, setIsRegenerating] = useState(false); 
  const [regeneratingMessage, setRegeneratingMessage] = useState("");
  
  // 履歴ナビゲーション用の状態
  const [prevVersionId, setPrevVersionId] = useState(null);
  const [nextVersionId, setNextVersionId] = useState(null);
  const [hasPrevVersion, setHasPrevVersion] = useState(false);
  const [hasNextVersion, setHasNextVersion] = useState(false);

  // 自動保存用の状態
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
  const autoSaveTimerRef = useRef(null);

  // 自動保存関数
  const autoSaveFormData = useCallback(async (formData, resultId) => {
    if (!formData || !resultId || uiMode !== 'main') return;

    try {
      setSaveStatus('saving');

      const response = await axios.post(
        `${API_BASE_URL}/media_selection/update`,
        formData,
        {
          params: { result_id: resultId },
          withCredentials: true
        }
      );

      if (response.data.status === 'success') {
        setSaveStatus('saved');
        // 保存済み表示は2秒後に自動的に消す
        setTimeout(() => {
          setSaveStatus('');
        }, 2000);
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('自動保存に失敗しました:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('unsaved'), 5000);
    }
  }, [uiMode]);

  // フォーム内容の変更を監視して自動保存
  useEffect(() => {
    if (uiMode !== 'main' || !formValues || !mediaResult?.resultId) return;

    // 初回表示時は保存しない（空のフォームの場合）
    if (!formValues.position || !formValues.advertisingBudget) return;

    setSaveStatus('unsaved');

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveFormData(formValues, mediaResult.resultId);
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formValues, mediaResult, uiMode, autoSaveFormData]);

  // Add activity tracking
  useEffect(() => {
    const cleanup = activityTracker.trackPageView('media_selection');
    return () => cleanup();
  }, []);

  // (A) 履歴から選択したときの処理
  const handleSelectHistoryItem = (item) => {
    if (item.gptResponse) {
      const planAData = item.gptResponse.planA || {};
      const planBData = item.gptResponse.planB || {};
      const adviceObj = {
        advice: item.gptResponse.recommendation || "",
        reason: item.gptResponse.reason || "",
        improvement: "(追加改善提案など)",
      };

      setMediaPlanA(planAData);
      setMediaPlanB(planBData);
      setAdviceObj(adviceObj);
    } else {
      setMediaPlanA(null);
      setMediaPlanB(null);
      setAdviceObj(null);
    }

    // 過去の入力内容があれば反映
    if (item.formData) {
      setFormValues(item.formData);
    } else {
      setFormValues({});
    }

    // mediaResult自体を更新しておけば "現在表示中" の判定に使える
    setMediaResult({
      resultId: item.id,
      gptAdvice: item.gptResponse
    });

    // メイン画面へ＆媒体選定結果タブに移動
    setUiMode('main');
    setTabValue(1);

    // 履歴ダイアログ閉じる
    setHistoryOpen(false);

    showSnackbar("過去の媒体選定結果を読み込みました。");
  };

  // ---- 「初めからやり直す」メニュー
  const handleClickRestart = () => {
    setUiMode('selection');
    setSelectedPlanId(null);
    setFormValues({});
    setMediaResult(null);
    setMessages([]);
    handleMenuClose();
    showSnackbar("初めからやり直しました。");
  };

  // ---- 「過去の出力結果を確認」メニュー
  const handleClickHistory = async () => {
    setHistoryOpen(true);
    handleMenuClose();
    try {
      const resp = await axios.get(`${API_BASE_URL}/media_selection/history`, {
        withCredentials: true
      });
      setHistoryList(resp.data);
    } catch (err) {
      alert('過去履歴の取得に失敗しました。');
    }
  };

  // ---- 削除ボタンクリック時
  const handleDeleteClick = (item, event) => {
    event.stopPropagation(); // 親要素のクリックイベントを防止
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  // ---- 削除実行
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);

    try {
      await axios.patch(
        `${API_BASE_URL}/media_selection/result/${itemToDelete.id}`,
        {},
        { withCredentials: true }
      );

      // 成功：履歴リストから削除
      setHistoryList(prev => prev.filter(item => item.id !== itemToDelete.id));

      // 削除したデータが現在表示中だった場合、ローカルストレージもクリア
      if (mediaResult && itemToDelete.id === mediaResult.resultId) {
        localStorage.removeItem('lastMediaSelection');
      }

      // 成功メッセージ
      showSnackbar('履歴を削除しました');

    } catch (error) {
      console.error('削除エラー:', error);
      showSnackbar('削除に失敗しました');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // ---- 「採用計画を決定」メニュー（デモ）
  const handleDecidePlan = async () => {
    // 例: mediaResult というStateに { resultId, ... } が格納されているとする
    if (!mediaResult?.resultId) {
      alert("決定する媒体選定のIDがありません。");
      return;
    }

    try {
      // decidePlan API に POST する
      await axios.post(
        `${API_BASE_URL}/media_selection/decide/${mediaResult.resultId}`,
        {},
        { withCredentials: true }
      );

      // mediaResult に決定フラグを設定（常に更新）
      if (mediaResult) {
        setMediaResult({ ...mediaResult, is_decided: true });
      }

      setDecideDialogOpen(true);
      showSnackbar("媒体選定を決定しました。");
    } catch (err) {
      console.error(err);
      alert("決定に失敗しました。");
    }
  };

  // ---- チャットモードのラベル（デモ用）
  const getChatModeLabel = (mode) => {
    switch (mode) {
      case 'sidebar': return 'サイドバー';
      case 'popup': return 'ポップアップ';
      default: return 'オフ';
    }
  };

  // (1) ユーザーが採用計画を選択したとき
  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
    setUiMode('loadingPlan');
  };

  // (A) 雑談チャット送信
  const handleChatSend = async () => {
    if (!mediaResult?.resultId) {
      alert("MediaSelectionResult のIDがありません");
      return;
    }
    if (!userInput.trim()) {
      alert("メッセージを入力してください");
      return;
    }

    setIsTyping(true);
    setBrushupLoading(true);

    try {
      const resp = await axios.post(
        `${API_BASE_URL}/media_selection/chat`,
        {
          resultId: mediaResult.resultId, // セッションID相当
          userMessage: userInput
        },
        { withCredentials: true }
      );

      // resp.data.messages => [{role:"assistant"|"user", content:"..."}...]
      setMessages(resp.data.messages.map(m => ({
        sender: m.role === 'assistant' ? 'bot' : 'user',
        text: m.content
      })));
      setUserInput('');
    } catch (err) {
      console.error("雑談API呼び出しエラー:", err);
      alert("雑談APIでエラーが発生しました。");
    } finally {
      setIsTyping(false);
      setBrushupLoading(false);
    }
  };

  // (B) 要件反映 (再生成)
  const handleReflectPlan = async () => {
    if (!mediaResult?.resultId) {
      alert("MediaSelectionResult のIDがありません");
      return;
    }
    if (!userInput.trim()) {
      alert("変更要望を入力してください");
      return;
    }

    setIsTyping(true);
    setBrushupLoading(true);

    try {
      // regenerate_selection エンドポイントへ
      const resp = await axios.post(
        `${API_BASE_URL}/media_selection/regenerate_selection`,
        {
          resultId: mediaResult.resultId,
          userMessage: userInput
        },
        { withCredentials: true }
      );

      // (1) planA, planB, adviceObj の更新
      setMediaPlanA(resp.data.planA);
      setMediaPlanB(resp.data.planB);
      setAdviceObj(resp.data.advice);

      // (2) チャット履歴更新
      if (resp.data.messages) {
        setMessages(
          resp.data.messages.map(m => ({
            sender: m.role === 'assistant' ? 'bot' : 'user',
            text: m.content
          }))
        );
      }

      // (3) フォーム入力内容更新
      //    updatedRequirements が返ってくるので formValues にマージ
      if (resp.data.updatedRequirements) {
        setFormValues(prev => ({
          ...prev,
          ...resp.data.updatedRequirements
        }));
      }

      // (4) 新しいmediaResultを設定 (新しいIDを使用)
      const newMediaResult = {
        resultId: resp.data.resultId,
        gptAdvice: {
          planA: resp.data.planA,
          planB: resp.data.planB,
          recommendation: resp.data.advice.recommendation || resp.data.advice.advice || "",
          reason: resp.data.advice.reason || "",
          merits: resp.data.advice.merits || "",
          demerits: resp.data.advice.demerits || ""
        }
      };
      setMediaResult(newMediaResult);

      // (5) 親IDを設定して前のバージョンへ戻れるようにする
      setPrevVersionId(resp.data.parentId);
      setHasPrevVersion(!!resp.data.parentId);
      
      // ナビゲーション情報を取得して更新
      await fetchNavigationInfo(resp.data.resultId);

      // (6) localStorage 保存など（scientificAnalysisも含める）
      saveToLocalStorage(
        resp.data.planA,
        resp.data.planB,
        resp.data.advice,
        { ...formValues, ...resp.data.updatedRequirements },
        resp.data.resultId,
        resp.data.scientificAnalysis || null
      );

      // 入力欄やローディングを解除
      setUserInput('');
      showSnackbar("要件を反映し、再生成しました。");

    } catch (err) {
      console.error("要件反映失敗:", err);
      alert("要件の再反映に失敗しました。");
    } finally {
      setIsTyping(false);
      setBrushupLoading(false);
    }
  };

  // ナビゲーション情報を取得する関数
  const fetchNavigationInfo = async (resultId) => {
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/media_selection/navigation/${resultId}`,
        { withCredentials: true }
      );
      
      // 前後バージョンの情報を設定
      setPrevVersionId(resp.data.prevId);
      setNextVersionId(resp.data.nextId);
      setHasPrevVersion(!!resp.data.prevId);
      setHasNextVersion(!!resp.data.nextId);
      
    } catch (err) {
      console.error("ナビゲーション情報取得エラー:", err);
      // エラー時は前後バージョンを無効化
      setHasPrevVersion(false);
      setHasNextVersion(false);
    }
  };

  // 前のバージョンに戻る
  const handlePrevVersion = async () => {
    if (!prevVersionId) {
      return;
    }
    
    setIsLoading(true);
    try {
      // 前のバージョンのデータを取得
      const resp = await axios.get(
        `${API_BASE_URL}/media_selection/history`,
        { withCredentials: true }
      );
      
      // 前のバージョンを検索
      const prevVersion = resp.data.find(item => item.id === prevVersionId);
      
      if (prevVersion) {
        // 前のバージョンのデータを表示
        handleSelectHistoryItem(prevVersion);
        // ナビゲーション情報も更新
        await fetchNavigationInfo(prevVersionId);
        showSnackbar("前のバージョンを表示しています");
      } else {
        alert("前のバージョンのデータが見つかりませんでした");
      }
    } catch (err) {
      console.error("前のバージョン取得エラー:", err);
      alert("前のバージョンの取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };
  
  // 次のバージョンに進む
  const handleNextVersion = async () => {
    if (!nextVersionId) {
      return;
    }
    
    setIsLoading(true);
    try {
      // 次のバージョンのデータを取得
      const resp = await axios.get(
        `${API_BASE_URL}/media_selection/history`,
        { withCredentials: true }
      );
      
      // 次のバージョンを検索
      const nextVersion = resp.data.find(item => item.id === nextVersionId);
      
      if (nextVersion) {
        // 次のバージョンのデータを表示
        handleSelectHistoryItem(nextVersion);
        // ナビゲーション情報も更新
        await fetchNavigationInfo(nextVersionId);
        showSnackbar("次のバージョンを表示しています");
      } else {
        alert("次のバージョンのデータが見つかりませんでした");
      }
    } catch (err) {
      console.error("次のバージョン取得エラー:", err);
      alert("次のバージョンの取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  // 履歴から選択したとき + メディア結果が変わったときにナビゲーション情報を取得
  useEffect(() => {
    if (mediaResult?.resultId) {
      fetchNavigationInfo(mediaResult.resultId);
    }
  }, [mediaResult?.resultId]);

  // (2) plan詳細取得 or 新規状態
  useEffect(() => {
    if (uiMode === 'loadingPlan') {
      if (selectedPlanId === null) {
        // 新規状態
        setFormValues({});
        setUiMode('form');
      } else {
        // 既存planIDに応じてAPI取得
        axios
          .get(`${BACKEND_URL}/media_selection/decided_plan/${selectedPlanId}`, { withCredentials: true })
          .then((resp) => {
            const data = resp.data;
            // 例：AI計画から計算した広告費などをセット
            const location = data.location || '';
            const position = data.position || '';
            const requirementTargetHires = data.requirement_targetHires || '';
            const totalBudget = data.requirement_budget || '0';
            let mediaPerc = data.plan_budgetMedia || 0;
            let advBudgetCalc = 0;
            try {
              const totalNum = parseInt(totalBudget, 10);
              advBudgetCalc = Math.floor((totalNum * mediaPerc) / 100);
            } catch (e) {
              advBudgetCalc = 0;
            }
            const finalAdvBudget = advBudgetCalc || data.advertising_budget || 0;

            // recruit_type に応じた targetCandidateGroup の設定
            let targetCandidateGroup = '';
            if (data.recruit_type === "both") {
              targetCandidateGroup = "新卒、中途";
            } else if (data.recruit_type === "newGrad") {
              targetCandidateGroup = "新卒";
            } else if (data.recruit_type === "midCareer") {
              targetCandidateGroup = "中途";
            }

            const newFormData = {
              position,
              targetHires: requirementTargetHires,
              advertisingBudget: finalAdvBudget.toString(),
              location,
              desiredCompletionDate: '',
              // 任意項目
              budgetUpperLimit: '',
              targetCandidateGroup: targetCandidateGroup || '',
              preferredMedia: data.preferred_media || '',
              adDuration: data.ad_duration || '',
              previousMediaUsage: data.previous_media_usage || '',
            };
            setFormValues(newFormData);
            setUiMode('form');
          })
          .catch((err) => {
            console.error(err);
            alert("プラン詳細の取得に失敗しました。");
            setUiMode('selectPlan');
          });
      }
    }
  }, [uiMode, selectedPlanId]);

  // (3) フォーム送信 → API呼び出し
  const handleFormSubmit = async (values) => {
    const endTracking = await activityTracker.trackFeatureUse('media_requirements_update', {
      requirements: values
    });
    try {
      // ロード画面にして isLoading = true
      setUiMode('loading');
      setFormValues(values);
      setIsLoading(true);
      setRegeneratingMessage("募集媒体を選定中...");

      if (USE_MOCK) {
        // === MOCK ===
        setTimeout(() => {
          const mockPlanA = {
            planName: "Aプラン(モック)",
            planSummary: "モック版Aプランの説明",
            mediaList: [
              { name: "媒体X", cost: 200000, expectedApplicants: 15, coverage: "全国", planComment: "...", advantage: "...", disadvantage: "..." }
            ]
          };
          const mockPlanB = {
            planName: "Bプラン(モック)",
            planSummary: "モック版Bプランの説明",
            mediaList: [
              { name: "媒体Y", cost: 80000, expectedApplicants: 8, coverage: "全国", planComment: "...", advantage: "...", disadvantage: "..." }
            ]
          };
          const mockAdvice = {
            advice: "いくつかの媒体を併用すると良いでしょう。(デモ)",
            reason: "複数媒体で応募母集団を増やす (デモ)",
            improvement: "SNS活用も検討してみてください。(デモ)"
          };

          setMediaPlanA(mockPlanA);
          setMediaPlanB(mockPlanB);
          setAdviceObj(mockAdvice);

          setUiMode('main');
          setTabValue(1); // 結果タブに切り替え
          showSnackbar("媒体選定を完了しました(デモ)");
          setIsLoading(false);
        }, 1500);
      } else {
        // === 実際のAPI呼び出し ===
        axios.post(`${BACKEND_URL}/media_selection/generate_selection`, values, { withCredentials: true })
          .then((resp) => {
            // resp.data => { planA, planB, gptAdvice, resultId } を想定
            const data = resp.data;
            // ローカル変数に取り出す
            const planAData = data.gptAdvice.planA;
            const planBData = data.gptAdvice.planB;
            const adviceObj = {
              advice: data.gptAdvice?.recommendation || "",
              reason: data.gptAdvice?.reason || "",
              improvement: "",
            };
            // scientificAnalysisを含めて保存
            const scientificAnalysis = data.gptAdvice?.scientificAnalysis || null;
            saveToLocalStorage(planAData, planBData, adviceObj, values, data.resultId, scientificAnalysis);

            // State をまとめて更新 (非同期)
            setMediaPlanA(planAData);
            setMediaPlanB(planBData);
            setAdviceObj(adviceObj);

            setMediaResult({
              resultId: data.resultId,
              gptAdvice: data.gptAdvice
            });

            setUiMode("main");
            setTabValue(1); // 結果タブに切り替え
            showSnackbar("媒体選定を完了しました。");
          })
          .catch((err) => {
            console.error(err);
            alert("媒体選定APIでエラーが発生しました。");
            setUiMode("form");
          })
          .finally(() => {
            setIsLoading(false);
            setIsRegenerating(false);
          });
      }
    } finally {
      endTracking();
    }
  };

  // (4) タブ制御
  const [tabValue, setTabValue] = useState(0);

  // 前回の媒体選定をlocalStorageから読み込む
  useEffect(() => {
    // 例えば "lastMediaSelection" というキーで保存していたとする
    const saved = localStorage.getItem("lastMediaSelection");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.mediaResult) {
          // parsed.mediaResult には { resultId, gptAdvice, ... } が入っている想定
          setMediaResult(parsed.mediaResult);

          // planA, planB, adviceObj
          if (parsed.mediaResult.gptAdvice) {
            const planAData = parsed.mediaResult.gptAdvice.planA || {};
            const planBData = parsed.mediaResult.gptAdvice.planB || {};
            const adviceObj = {
              advice: parsed.mediaResult.gptAdvice.recommendation || "",
              reason: parsed.mediaResult.gptAdvice.reason || "",
              improvement: "",
            };
            setMediaPlanA(planAData);
            setMediaPlanB(planBData);
            setAdviceObj(adviceObj);
          }
          // 過去の formData も保存していれば取り出す
          if (parsed.formValues) {
            setFormValues(parsed.formValues);
          }

          // "main" 表示にして結果をすぐに見せる
          setUiMode("main");
          setTabValue(1); // 媒体選定結果タブに移動
        }
      } catch (e) {
        console.warn("Failed to parse lastMediaSelection:", e);
      }
    }
  }, []); // 初回マウント時のみ

  return (
    <AppLayout>
      <Box
        sx={{
          marginRight: !isSmDown && chatMode === 'sidebar' ? '400px' : 0,
          transition: 'margin-right 0.3s ease',
        }}
      >

          {/* AIアシスト機能ボタン */}
          {uiMode === 'main' && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Tooltip
                title={
                  chatMode === 'off' ? 'AIに相談' :
                  chatMode === 'sidebar' ? 'サイドバーチャット（クリックでポップアップに切替）' :
                  'ポップアップチャット（クリックでオフに切替）'
                }
                arrow
              >
                <IconButton
                  onClick={() => {
                    // チャットモード切り替え
                    if (chatMode === 'sidebar') setChatMode('popup');
                    else if (chatMode === 'popup') setChatMode('off');
                    else setChatMode('sidebar');
                  }}
                  sx={chatIconButton('media_selection', chatMode !== 'off')}
                >
                  <ForumIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="その他の操作" arrow>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={menuIconButton('media_selection')}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* UIモードごとの表示 */}
          {uiMode === 'selection' && (
            <Container maxWidth="lg" sx={{ py: 6 }}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                sx={{ textAlign: 'center', mb: 6 }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: mediaSelectionTheme.primary,
                  }}
                >
                  募集媒体選定の利用方法を選択
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  以下の2つの方法から募集媒体選定の方法を選んでください
                </Typography>
              </Box>

              <Grid container spacing={4} justifyContent="center" sx={{ mb: 4 }}>
                {/* 新規作成カード */}
                <Grid item xs={12} md={6}>
                  <Paper
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ y: -8 }}
                    onClick={() => setUiMode('selectPlan')}
                    sx={{
                      ...selectionCard('media_selection', theme),
                      minHeight: 400,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                      {/* Icon */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '12px',
                            backgroundColor: `${mediaSelectionTheme.primary}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <RestartAltIcon sx={{ fontSize: 32, color: mediaSelectionTheme.primary }} />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: '#212529',
                            mb: 2,
                          }}
                        >
                          新規作成
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 3,
                          }}
                        >
                          新しく募集媒体を選定します。採用計画から情報を取得するか、直接入力して最適な募集媒体を選定します。
                        </Typography>

                        <Box sx={{
                          p: 2,
                          bgcolor: `${mediaSelectionTheme.primary}08`,
                          borderRadius: 2,
                          textAlign: 'left'
                        }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, color: mediaSelectionTheme.primary, fontWeight: 600 }}>
                            主な入力項目:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {['募集職種', '採用人数', '広告予算', '勤務地', '完了希望日', 'ターゲット層'].map((item) => (
                              <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: mediaSelectionTheme.primary, mr: 0.75 }}/>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>

                      {/* Action Button */}
                      <Button
                        variant="contained"
                        sx={{
                          ...selectionCardButton('media_selection'),
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUiMode('selectPlan');
                        }}
                      >
                        この方法で作成する
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* 過去の選定結果から選択カード */}
                <Grid item xs={12} md={6}>
                  <Paper
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    whileHover={{ y: -8 }}
                    onClick={handleClickHistory}
                    sx={{
                      ...selectionCard('media_selection', theme),
                      minHeight: 400,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                      {/* Icon */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '12px',
                            backgroundColor: `${mediaSelectionTheme.primary}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <HistoryIcon sx={{ fontSize: 32, color: mediaSelectionTheme.primary }} />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: '#212529',
                            mb: 2,
                          }}
                        >
                          過去の選定結果から選択
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 3,
                          }}
                        >
                          過去に選定した募集媒体を確認・再利用します。履歴から選択して、必要に応じて修正できます。
                        </Typography>

                        <Box sx={{
                          p: 2,
                          bgcolor: `${mediaSelectionTheme.primary}08`,
                          borderRadius: 2,
                          textAlign: 'left'
                        }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, color: mediaSelectionTheme.primary, fontWeight: 600 }}>
                            できること:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {['過去の選定結果を確認', '決定済み媒体を再利用', '選定結果を修正', '履歴を検索'].map((item) => (
                              <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: mediaSelectionTheme.primary, mr: 0.75 }}/>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>

                      {/* Action Button */}
                      <Button
                        variant="contained"
                        sx={{
                          ...selectionCardButton('media_selection'),
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClickHistory();
                        }}
                      >
                        この方法で選択する
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          )}

          {uiMode === 'selectPlan' && (
            <SelectPlanScreen onSelectPlan={handleSelectPlan} />
          )}
          {uiMode === 'loadingPlan' && <LoadingScreen
            message="募集媒体の情報を読み込み中..."
            loadingType="media_selection"
            featureKey="media_selection"
          />}
          {uiMode === 'form' && (
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>
              <MediaSelectionForm defaultValues={formValues} onSubmit={handleFormSubmit} onBack={() => setUiMode('selection')} />
            </Box>
          )}
          {uiMode === 'loading' && (
            isRegenerating ? (
              <LoadingScreen
                variant="overlay"
                message="媒体選定を再生成中..."
                loadingType="media_selection"
                featureKey="media_selection"
              />
            ) : (
              <LoadingScreen
                message="募集媒体を選定中..."
                loadingType="media_selection"
                featureKey="media_selection"
              />
            )
          )}
          {uiMode === 'main' && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                py: 3,
                px: { xs: 2, md: 3 },
                backgroundColor: bg(theme).card,
                maxWidth: 1200,
                mx: 'auto'
              }}
            >
              {/* ステータスバー */}
              <AnimatePresence>
                {mediaResult && (
                  <Alert
                    component={motion.div}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    severity={mediaResult.is_decided ? "success" : "info"}
                    icon={mediaResult.is_decided ? <CheckCircleOutlineIcon /> : <InfoOutlinedIcon />}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {mediaResult.is_decided ? 'この媒体選定は決定済みです' : 'この媒体選定は未決定です'}
                      </Typography>
                      <Typography variant="body2">
                        {mediaResult.is_decided
                          ? '求人票作成時にこの選定結果を活用でき、掲載内容の最適化・参照が可能です'
                          : '決定すると求人票作成時にこの選定結果を活用でき、掲載内容の最適化ができます'}
                      </Typography>
                    </Box>
                  </Alert>
                )}
              </AnimatePresence>

              <NiceTabsExample tabValue={tabValue} setTabValue={setTabValue} />

              {/* 自動保存ステータス表示 - 必要な時だけ表示 */}
              {tabValue === 0 && saveStatus && saveStatus !== '' && (
                <Box
                  sx={{
                    position: 'fixed',
                    top: 80,
                    right: 20,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: adaptiveColor(theme, 'rgba(255,255,255,0.95)', 'rgba(30,30,30,0.95)'),
                    padding: '8px 16px',
                    borderRadius: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000
                  }}
                >
                  {saveStatus === 'saving' && (
                    <>
                      <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                      <Typography variant="caption">保存中...</Typography>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="caption">保存済み</Typography>
                    </>
                  )}
                  {saveStatus === 'unsaved' && (
                    <>
                      <SaveIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      <Typography variant="caption">未保存の変更</Typography>
                    </>
                  )}
                  {saveStatus === 'error' && (
                    <>
                      <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      <Typography variant="caption">保存エラー</Typography>
                    </>
                  )}
                </Box>
              )}

              {/* タブ 0: 入力内容 */}
              {tabValue === 0 && (
                <Box sx={{ mb: 4 }}>
                  <RequirementsView 
                    requirements={{
                      ...formValues,
                      resultId: mediaResult?.resultId
                    }}
                    onLoadingStateChange={(loading) => {
                      // 再選定中のローディング状態を管理
                      setIsLoading(loading);
                      setIsRegenerating(loading);
                      
                      if (loading) {
                        setRegeneratingMessage("媒体選定を再生成中...");
                        setUiMode('loading');
                      } else if (uiMode === 'loading') {
                        // ローディングが終わったら必ずmainモードに戻して結果タブに切り替え
                        setUiMode('main');
                        setTabValue(1); // 結果タブに切り替え
                      }
                    }}
                    onRequirementsUpdated={(data, updatedFormData) => {
                      // Update the form values with the edited data
                      setFormValues(updatedFormData);
                      
                      // Update the media plans and advice with the regenerated data
                      if (data.planA) setMediaPlanA(data.planA);
                      if (data.planB) setMediaPlanB(data.planB);
                      if (data.advice) setAdviceObj(data.advice);
                      
                      // Update mediaResult with the new resultId if available
                      const updatedMediaResult = {
                        resultId: data.resultId || mediaResult?.resultId,
                        gptAdvice: {
                          planA: data.planA,
                          planB: data.planB,
                          recommendation: data.advice?.recommendation || "",
                          reason: data.advice?.reason || "",
                          merits: data.advice?.merits || "",
                          demerits: data.advice?.demerits || "",
                          scientificAnalysis: data.scientificAnalysis || null
                        }
                      };
                      setMediaResult(updatedMediaResult);

                      // Save to localStorage（scientificAnalysisも含める）
                      saveToLocalStorage(
                        data.planA,
                        data.planB,
                        data.advice,
                        updatedFormData,
                        data.resultId || mediaResult?.resultId,
                        data.scientificAnalysis || null
                      );
                      
                      // Fetch navigation info if new resultId is available
                      if (data.resultId) {
                        fetchNavigationInfo(data.resultId);
                      }
                      
                      // Show success message and ensure we're in main mode with results tab
                      showSnackbar("媒体選定を再生成しました。");
                      setUiMode('main');
                      setTabValue(1);
                      
                      // Force loading states to false
                      setIsLoading(false);
                      setIsRegenerating(false);
                    }}
                  />
                </Box>
              )}

              {/* タブ 1: 媒体選定結果 */}
              {tabValue === 1 && (
                <Box sx={{ mb: 4 }}>
                  <MediaSelectionResult
                    isLoading={isLoading}
                    planA={mediaPlanA}
                    planB={mediaPlanB}
                    advice={adviceObj}
                    scientificAnalysis={mediaResult?.gptAdvice?.scientificAnalysis}
                  />
                </Box>
              )}

              {/* FAB - 未決定の場合のみ表示 */}
              {mediaResult && !mediaResult.is_decided && (
                <Fab
                  component={motion.button}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  variant="extended"
                  onClick={handleDecidePlan}
                  sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    backgroundColor: '#2196F3',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: '#1976D2',
                      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <CheckIcon sx={{ mr: 1 }} />
                  この選定を決定
                </Fab>
              )}
            </Box>
          )}
        </Box>

        {/* チャット (サイドバー/ポップアップ) */}
        {chatMode === 'popup' && (
          <AIChat
            messages={messages}
            userInput={userInput}
            setUserInput={setUserInput}
            isTyping={isTyping}
            isLoading={brushupLoading}
            chatEndRef={chatEndRef}
            onChatSend={handleChatSend}
            onReflectPlan={handleReflectPlan}
            initialGuideMessage="AIポップアップチャットです。何か質問を入力してみてください。"
            chatButtonLabel="AIに相談"
            planButtonLabel="媒体選定を修正"
            featureKey="media_selection"
          />
        )}
        {chatMode === 'sidebar' && (
          <AIChatSide
            messages={messages}
            userInput={userInput}
            setUserInput={setUserInput}
            isTyping={isTyping}
            isLoading={brushupLoading}
            onChatSend={handleChatSend}
            onReflectPlan={handleReflectPlan}
            chatEndRef={chatEndRef}
            initialGuideMessage="サイドチャットでいつでもAIに相談できます。"
            onClose={() => setChatMode('popup')}
            chatButtonLabel="AIに相談"
            planButtonLabel="媒体選定を修正"
            featureKey="media_selection"
          />
        )}

      {/* AssistMenu (アシストメニュー) */}
      <AssistMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onRestart={handleClickRestart}
        onShowHistory={handleClickHistory}
        onDecide={handleDecidePlan}
        onPrevVersion={handlePrevVersion}
        onNextVersion={handleNextVersion}
        hasPrevVersion={hasPrevVersion}
        hasNextVersion={hasNextVersion}
      />

      {/* 採用計画決定ダイアログ */}
      <Dialog open={decideDialogOpen} onClose={handleCloseDecideDialog}>
        <DialogTitle>採用計画を確定しました</DialogTitle>
        <DialogContent>
          <Typography>
            現在表示中の媒体選定を確定しました。<br />
            (再度決定する場合は他のプランを決定すると上書きされます)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDecideDialog}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* 履歴ダイアログ - Timeline形式 */}
      <Dialog open={historyOpen} onClose={handleCloseHistory} fullWidth maxWidth="md">
        {/* ヘッダー */}
        <Box
          sx={{
            backgroundColor: '#2196F3',
            color: 'white',
            px: 3,
            py: 3
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            過去の媒体選定履歴
          </Typography>

          {/* 検索バー */}
          <TextField
            fullWidth
            placeholder="キーワードで検索..."
            size="small"
            value={historySearchQuery}
            onChange={(e) => setHistorySearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: alpha('#ffffff', 0.2),
                borderRadius: 3,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.25)
                },
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.8),
                  opacity: 1
                },
                '& input': {
                  color: 'white'
                }
              }
            }}
          />
        </Box>

        <DialogContent sx={{ p: 3, backgroundColor: bg(theme).subtle }}>
          {historyList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <AccessTimeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                履歴がありません
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* Timeline縦線 */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 24,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  backgroundColor: '#2196F3',
                  opacity: 0.25
                }}
              />

              {/* Timeline Items */}
              <AnimatePresence>
                {historyList
                  .filter((item) => {
                    if (!historySearchQuery.trim()) return true;
                    const query = historySearchQuery.toLowerCase();
                    const aSummary = (item.planASummary || '').toLowerCase();
                    const bSummary = (item.planBSummary || '').toLowerCase();
                    const sessionId = String(item.id || '');

                    return aSummary.includes(query) ||
                           bSummary.includes(query) ||
                           sessionId.includes(query);
                  })
                  .map((item, index) => {
                  const aSummary = item.planASummary || "";
                  const bSummary = item.planBSummary || "";

                  let planSummary;
                  if (!aSummary && !bSummary) {
                    planSummary = (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        プランデータがありません
                      </Typography>
                    );
                  } else {
                    planSummary = (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {aSummary && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Aプラン
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                              {aSummary.slice(0, 80)}...
                            </Typography>
                          </Box>
                        )}
                        {bSummary && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Bプラン
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                              {bSummary.slice(0, 80)}...
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  const isCurrent = mediaResult && mediaResult.resultId === item.id;
                  const itemDate = new Date(item.timestamp || item.created_at);

                  return (
                    <Box
                      key={item.id}
                      component={motion.div}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.1,
                        ease: [0.4, 0, 0.2, 1]
                      }}
                      sx={{ position: 'relative', pl: 7, pb: 4, '&:last-child': { pb: 0 } }}
                    >
                      {/* Timeline Icon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 12,
                          top: 0,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: item.is_decided ? '#4caf50' : '#2196F3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                          zIndex: 1
                        }}
                      >
                        {item.is_decided ? (
                          <CheckIcon sx={{ fontSize: 14, color: 'white' }} />
                        ) : (
                          <AccessTimeIcon sx={{ fontSize: 14, color: 'white' }} />
                        )}
                      </Box>

                      {/* Timeline Card */}
                      <Box
                        component={motion.div}
                        whileHover={{ y: -4, transition: { duration: 0.2 } }}
                        sx={{
                          position: 'relative',
                          backgroundColor: bg(theme).card,
                          borderRadius: 2,
                          p: 2.5,
                          boxShadow: shadow(theme).sm,
                          border: '1px solid',
                          borderColor: item.is_decided ? 'success.light' : 'divider',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            borderColor: item.is_decided ? 'success.main' : 'primary.main'
                          }
                        }}
                      >
                        {/* Card Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <CampaignIcon sx={{ fontSize: 18, color: '#2196F3' }} />
                              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                媒体選定 #{item.id}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {itemDate.toLocaleDateString('ja-JP', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                timeZone: 'Asia/Tokyo'
                              })}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {item.is_decided && (
                              <Chip
                                label="決定済み"
                                size="small"
                                icon={<CheckIcon />}
                                sx={{
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  fontWeight: 600,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                }}
                              />
                            )}
                            {isCurrent && (
                              <Chip
                                label="表示中"
                                size="small"
                                sx={{
                                  bgcolor: alpha('#ef5350', 0.1),
                                  color: '#ef5350',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Card Content */}
                        {planSummary}

                        {/* Card Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2 }}>
                          <Button
                            variant={isCurrent ? "outlined" : "contained"}
                            size="small"
                            disabled={isCurrent}
                            onClick={() => handleSelectHistoryItem(item)}
                            sx={{
                              borderRadius: 20,
                              px: 3,
                              fontWeight: 600,
                              textTransform: 'none',
                              ...(!isCurrent && {
                                backgroundColor: '#2196F3',
                                '&:hover': {
                                  backgroundColor: '#1976D2',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }
                              })
                            }}
                          >
                            {isCurrent ? "表示中" : "この結果を表示"}
                          </Button>

                          {/* 削除ボタン（現在表示中でない場合のみ表示） */}
                          {!isCurrent && (
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(item, e)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </AnimatePresence>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: bg(theme).subtle, borderTop: `1px solid ${border(theme).default}` }}>
          <Button onClick={handleCloseHistory} variant="outlined" sx={{ borderRadius: 20, px: 3 }}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          履歴を削除しますか？
        </DialogTitle>

        <DialogContent>
          {itemToDelete && (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>媒体選定 ID:</strong> {itemToDelete.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>作成日:</strong> {new Date(itemToDelete.created_at).toLocaleDateString('ja-JP', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'Asia/Tokyo'
                })}
              </Typography>

              <Alert severity="warning" sx={{ mt: 2 }}>
                削除した履歴は復元できません
              </Alert>

              {itemToDelete.is_decided && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  このデータは決定済みです。本当に削除しますか？
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isDeleting ? '削除中...' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar通知 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AppLayout>
  );
}